import cls from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { RadioInput } from '@/components/atoms/RadioSwitch/RadioInput';
import { P, Span } from '@/components/atoms/Typography/Typography';
import nameSpacesConfig from '@/config/config.nameSpaces.json';
import { FormValues } from '@/containers/ConfigurePage/components/FormConfig/formData';
import { ProductConfig } from '@/data/productsConfig';
import { useData } from '@/lib/pageData/pageDataContext';
import { formatPrice } from '@/utils/formatPrice';
import styles from './ChooseMembershipRadioInput.module.scss';
import type { Membership } from './hooks/useMemberships';

interface Props {
  currentFormValues: Partial<FormValues>;
  membership: Membership;
  productConfig: ProductConfig;
}

export const ChooseMembershipRadioInput = ({
  currentFormValues,
  membership,
  productConfig,
}: Props) => {
  const { currency } = useData<'default'>();
  const { locale } = useRouter();
  const { t } = useTranslation([
    nameSpacesConfig.pages,
    nameSpacesConfig.product,
  ]);

  const { durationMonths, rates } = membership;
  const durationLabel =
    durationMonths === '1' ? 'flexible' : `${durationMonths}months`;

  return (
    <>
      {durationMonths === '12' && !rates.specialOffer && (
        <div
          className={cls(styles.bestValueLabel, {
            [styles.disabled]: currentFormValues.durationMonths !== '12',
          })}
        >
          <span>{t('pages:configure.membership.bestValueLabel')}</span>
        </div>
      )}
      {durationMonths !== '1' && !!rates.specialOffer && (
        <div
          className={cls(styles.bestValueLabel, {
            [styles.disabled]:
              currentFormValues.durationMonths !== durationMonths,
          })}
        >
          <span>
            {t(
              `pages:configure.membership.specialOffer.${productConfig.translationKey}.membership.${durationLabel}.label`
            )}
          </span>
        </div>
      )}
      <RadioInput
        className={styles.radioLabel}
        aria-label={t(`configure.membership.${durationLabel}.label`)}
        value={durationMonths}
      >
        <>
          <div className={styles.radioText}>
            <div className={styles.priceTagContainer}>
              <P className={styles.price}>
                {!!rates.specialOffer && (
                  <Span className={styles.strikePrice} color="strike_through">
                    {formatPrice(rates.normal, locale!, currency)}
                  </Span>
                )}
                <Span className={styles.normalPrice}>
                  {t('pages:configure.membership.priceLabel', {
                    price: formatPrice(
                      rates.specialOffer || rates.normal,
                      locale!,
                      currency
                    ),
                  })}
                  {!!(rates.specialOffer || rates.discount) && '*'}
                </Span>
              </P>
              {rates.discount && (
                <span
                  className={cls(styles.discountTag, {
                    [styles.selected]:
                      currentFormValues.durationMonths === durationMonths,
                  })}
                >
                  {t('pages:configure.membership.discountLabel', {
                    percentage: rates.discount,
                  })}
                </span>
              )}
            </div>
            <Span version="body1">
              {t(`configure.membership.${durationLabel}.label`)}
            </Span>
          </div>
        </>
      </RadioInput>
    </>
  );
};
